import { useMemo } from 'react';
import { useTheme } from 'styled-components';

import { MedicalEvent, MedicalEventStatus } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { createSingleInterpolatedTranslationKey } from '@dreryk/react-common';
import {
   CheckIcon,
   HourglassBottomIcon,
   HourglassTopIcon,
   NotIcon,
   PatientStatusOptions,
   StatusIcon,
   StatusIconTooltip,
   TestId,
   TooltipPlacement,
} from '@dreryk/react-components';

import { patientMedicalInformationTranslationKeys } from '../../translations';

type MedicalEventCellProps = {
   readonly medicalEvent: MedicalEvent | undefined;
   readonly testId: TestId;
};

const useMedicalEventOptions = (): PatientStatusOptions<Record<MedicalEventStatus, unknown>> => {
   const theme = useTheme();
   return useMemo(
      () => ({
         REGISTERED: {
            icon: <CheckIcon />,
            backgroundColor: theme.success,
         },
         ERROR: {
            icon: <NotIcon />,
            backgroundColor: theme.error,
         },
         PENDING: {
            icon: <HourglassTopIcon />,
            backgroundColor: theme.warning,
         },
         OUTDATED: {
            icon: <HourglassBottomIcon />,
            backgroundColor: theme.error,
         },
         NONE: {
            icon: <NotIcon />,
            backgroundColor: theme.background,
         },
      }),
      [theme]
   );
};

export const MedicalEventCell = (props: MedicalEventCellProps): JSX.Element => {
   const options = useMedicalEventOptions();
   const status = props.medicalEvent?.status ?? 'NONE';

   return (
      <StatusIconTooltip
         tooltipText={createSingleInterpolatedTranslationKey(
            patientMedicalInformationTranslationKeys.gynecologicalInterview.medicalEventStatus[status],
            'errorMessage',
            props.medicalEvent?.errorMessage
         )}
         tooltipPlacement={TooltipPlacement.TOP}
         testId={'tooltip'}
      >
         <StatusIcon type={status} options={options} testId={props.testId} />
      </StatusIconTooltip>
   );
};
